import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCurrentUser, logout as apiLogout, User as ApiUser, StatsigBootstrapData } from '../api/auth';
import { isNetworkOrTimeoutError } from '../utils/networkError';

export interface User {
  id: string;
  email: string;
  name?: string;
  avatarUrl?: string;
  subscriptionTier?: string;
  emailVerified?: boolean;
  createdAt: string;
}

/** Minimal user from login/register response (no image, subscriptionTier, createdAt) */
export interface LoginUser {
  id: string;
  email: string;
  name: string | null;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
  /** Set user from login/register response so redirect works without waiting for /api/auth/me */
  setUserFromLogin: (loginUser: LoginUser) => void;
  /** Statsig bootstrap data returned from /api/auth/me (null until fetched). */
  statsigBootstrap: StatsigBootstrapData | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Map API user to internal user format
 */
function mapApiUser(apiUser: ApiUser): User {
  return {
    id: apiUser.id,
    email: apiUser.email,
    name: apiUser.name ?? undefined,
    avatarUrl: apiUser.image ?? undefined, // Better Auth uses 'image', we use 'avatarUrl'
    subscriptionTier: apiUser.subscriptionTier,
    createdAt: String(apiUser.createdAt),
  };
}

function loginUserToUser(loginUser: LoginUser): User {
  return {
    id: loginUser.id,
    email: loginUser.email,
    name: loginUser.name ?? undefined,
    avatarUrl: undefined,
    subscriptionTier: 'free',
    createdAt: new Date().toISOString(),
  };
}

// Matches seed-alice-000000000 in the database seed — run `pnpm seed` once to populate.
const PREVIEW_USER: User = {
  id: 'seed-alice-000000000',
  email: 'alice@example.com',
  name: 'Alice Example',
  subscriptionTier: 'pro',
  createdAt: new Date().toISOString(),
};

const isBypassAuth = import.meta.env.VITE_BYPASS_AUTH === 'true';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(isBypassAuth ? PREVIEW_USER : null);
  const [isLoading, setIsLoading] = useState(!isBypassAuth);
  const [statsigBootstrap, setStatsigBootstrap] = useState<StatsigBootstrapData | null>(null);

  const fetchUser = useCallback(async () => {
    if (isBypassAuth) return;
    try {
      setIsLoading(true);
      const currentUser = await getCurrentUser();
      setUser(currentUser ? mapApiUser(currentUser) : null);
      setStatsigBootstrap(currentUser?.statsig ?? null);
    } catch (error) {
      if (!isNetworkOrTimeoutError(error)) {
        console.error('Failed to fetch current user:', error);
      }
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    if (isBypassAuth) return;
    try {
      await apiLogout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null);
    }
  }, []);

  const refetch = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  const setUserFromLogin = useCallback((loginUser: LoginUser) => {
    setUser(loginUserToUser(loginUser));
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Listen for 401 from fetchApi — clear session and redirect to login
  useEffect(() => {
    if (isBypassAuth) return;
    const handleExpired = () => {
      setUser(null);
    };
    window.addEventListener('danny:session-expired', handleExpired);
    return () => window.removeEventListener('danny:session-expired', handleExpired);
  }, []);

  // Listen for login-complete from other tabs/iframes (e.g. extension side panel)
  // OAuth opens a new tab; this lets the iframe pick up the session without a reload
  useEffect(() => {
    if (isBypassAuth) return;
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('danny-auth');
      bc.onmessage = (event: MessageEvent) => {
        if (event.data?.type === 'login-complete') {
          void fetchUser();
        }
      };
    } catch {
      // BroadcastChannel not supported — extension will still work after manual refresh
    }
    return () => bc?.close();
  }, [fetchUser]);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    logout,
    refetch,
    setUserFromLogin,
    statsigBootstrap,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
